import api from './api';

export type LegalBlock =
  | { type: 'h'; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] };

export type LegalTemplate = {
  key: string;
  version: string;
  kind: 'contract' | 'consent';
  title: string;
  updatedAt: string;
  blocks: LegalBlock[];
};

export type LegalAcceptedItem = { key: string; version: string };

export const PURCHASE_CONSENT_KEYS = ['terms-of-use', 'privacy-policy', 'refund-policy'] as const;
export const PLATFORM_SERVICE_CONTRACT_KEY = 'platform-service-contract';

export type SignedDocumentSummary = {
  id: string;
  title: string;
  signedAt: string;
  templateKey: string;
  templateVersion?: string;
  kind?: string;
  signerName?: string;
  signerEmail?: string;
  pdfHash?: string;
  organizationName?: string;
  organizationId?: string;
};

export type ContractCoverageRow = {
  parishId: string;
  parishName: string;
  email: string;
  city?: string;
  state?: string;
  signedVersion: string | null;
  signedAt: string | null;
  signedDocumentId: string | null;
  currentVersion: string;
  isCurrent: boolean;
  pendingRequest: boolean;
};

export type ContractSignatureRequestRow = {
  id: string;
  parishId: string;
  parishName: string;
  email: string;
  templateKey: string;
  templateVersion: string;
  status: string;
  audience: string;
  sentAt: string;
  expiresAt: string;
  signedAt?: string;
  signedDocumentId?: string | null;
};

export const legalService = {
  getTemplate: async (key: string, vars: Record<string, string> = {}) => {
    const response = await api.get<{ success: boolean; data: LegalTemplate }>(`/public/legal/templates/${key}`, {
      params: vars,
    });
    return response.data.data;
  },
  listTemplates: async (vars: Record<string, string> = {}) => {
    const response = await api.get<{ success: boolean; data: LegalTemplate[] }>('/public/legal/templates', {
      params: vars,
    });
    return response.data.data || [];
  },
  listSignedDocuments: async (params?: { relatedType?: string; relatedId?: string; templateKey?: string; kind?: string }) => {
    const response = await api.get<{ success: boolean; data: SignedDocumentSummary[] }>(
      '/admin/signed-documents',
      { params }
    );
    return response.data.data || [];
  },
  getSignedDocumentAdmin: async (id: string) => {
    const response = await api.get<{
      success: boolean;
      data: SignedDocumentSummary & { bodySnapshot?: LegalBlock[]; parties?: unknown; ip?: string; userAgent?: string };
    }>(`/admin/signed-documents/${id}`);
    return response.data.data;
  },
  listLegalCatalogAdmin: async () => {
    const response = await api.get<{ success: boolean; data: LegalTemplate[] }>('/admin/legal/catalog');
    return response.data.data || [];
  },
  listContractCoverage: async (templateKey = PLATFORM_SERVICE_CONTRACT_KEY) => {
    const response = await api.get<{
      success: boolean;
      data: ContractCoverageRow[];
      summary: { templateKey: string; currentVersion: string; total: number; current: number; outdated: number; pending: number };
    }>('/admin/legal/coverage', { params: { templateKey } });
    return response.data;
  },
  listContractSignatureRequests: async (status?: string) => {
    const response = await api.get<{ success: boolean; data: ContractSignatureRequestRow[] }>(
      '/admin/legal/signature-requests',
      { params: status ? { status } : undefined }
    );
    return response.data.data || [];
  },
  sendContractSignatureRequests: async (payload: {
    templateKey?: string;
    audience: 'all' | 'selected' | 'outdated';
    parishIds?: string[];
    note?: string;
  }) => {
    const response = await api.post<{
      success: boolean;
      data: { sent: number; skipped: number; results: Array<{ parishName: string; status: string; message?: string }> };
    }>('/admin/legal/signature-requests/send', payload);
    return response.data;
  },
  getPublicSignatureRequest: async (token: string) => {
    const response = await api.get<{ success: boolean; data: any }>(`/public/legal/signature-requests/${token}`);
    return response.data.data;
  },
  signPublicSignatureRequest: async (
    token: string,
    payload: { signatureName: string; contractViewed: boolean; legalAccepted: LegalAcceptedItem[] }
  ) => {
    const response = await api.post(`/public/legal/signature-requests/${token}/sign`, payload);
    return response.data;
  },
  downloadSignedDocumentAdmin: async (id: string) => {
    const response = await api.get(`/admin/signed-documents/${id}/download`, { responseType: 'blob' });
    return response.data as Blob;
  },
  listMySignedDocuments: async () => {
    const response = await api.get<{ success: boolean; data: SignedDocumentSummary[] }>(
      '/dashboard/signed-documents'
    );
    return response.data.data || [];
  },
  downloadMySignedDocument: async (id: string) => {
    const response = await api.get(`/dashboard/signed-documents/${id}/download`, { responseType: 'blob' });
    return response.data as Blob;
  },
  signMyPlatformContract: async (payload: {
    signatureName: string;
    contractViewed: boolean;
    legalAccepted: LegalAcceptedItem[];
  }) => {
    const response = await api.post<{ success: boolean; data: SignedDocumentSummary; alreadySigned?: boolean }>(
      '/dashboard/signed-documents/platform-service-contract',
      payload
    );
    return response.data;
  },
};

export function purchaseAcceptedFromTemplates(templates: LegalTemplate[]): LegalAcceptedItem[] {
  return PURCHASE_CONSENT_KEYS.map((key) => {
    const found = templates.find((t) => t.key === key);
    return { key, version: found?.version || '1.0' };
  });
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
